import type { CanonicalCaptureMetadata } from './types.js';

export interface ExtractedJpegMetadata {
  orientation: CanonicalCaptureMetadata['orientation'];
  capturedAt?: string;
  hadGps: boolean;
}

const text = new TextDecoder('ascii');

/** Decode a copy with neutral EXIF; keep the selected original and its metadata untouched. */
export function neutralizeJpegOrientation(bytes: Uint8Array): Uint8Array {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return bytes;
  for (let offset = 2; offset + 4 <= bytes.length;) {
    if (bytes[offset] !== 0xff) break;
    const marker = bytes[offset + 1];
    if (marker === 0xda || marker === 0xd9) break;
    const length = (bytes[offset + 2]! << 8) | bytes[offset + 3]!;
    const end = offset + 2 + length;
    if (length < 2 || end > bytes.length) break;
    if (marker === 0xe1 && text.decode(bytes.subarray(offset + 4, offset + 10)) === 'Exif\u0000\u0000') {
      const start = offset + 10;
      if (start + 8 > end) break;
      const order = text.decode(bytes.subarray(start, start + 2));
      if (order !== 'II' && order !== 'MM') break;
      const little = order === 'II';
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      if (view.getUint16(start + 2, little) !== 42) break;
      const firstIfd = start + view.getUint32(start + 4, little);
      if (firstIfd < start + 8 || firstIfd + 2 > end) break;
      const entries = Math.min(view.getUint16(firstIfd, little), 512);
      for (let index = 0; index < entries; index += 1) {
        const entry = firstIfd + 2 + index * 12;
        if (entry + 12 > end) break;
        if (view.getUint16(entry, little) !== 0x0112 || view.getUint16(entry + 2, little) !== 3 ||
          view.getUint32(entry + 4, little) !== 1) continue;
        const value = view.getUint16(entry + 8, little);
        if (value < 2 || value > 8) return bytes;
        const copy = bytes.slice();
        new DataView(copy.buffer).setUint16(entry + 8, 1, little);
        return copy;
      }
      return bytes;
    }
    offset = end;
  }
  return bytes;
}

/** Reads only the fields needed before re-encoding. Re-encoded variants contain no EXIF. */
export function extractJpegMetadata(bytes: Uint8Array): ExtractedJpegMetadata {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return { orientation: 1, hadGps: false };
  for (let offset = 2; offset + 4 < bytes.length;) {
    if (bytes[offset] !== 0xff) break;
    const marker = bytes[offset + 1];
    const length = (bytes[offset + 2]! << 8) | bytes[offset + 3]!;
    if (
      marker === 0xe1 &&
      text.decode(bytes.subarray(offset + 4, offset + 10)) === 'Exif\u0000\u0000'
    ) {
      return parseTiff(bytes, offset + 10, offset + 2 + length);
    }
    if (length < 2) break;
    offset += 2 + length;
  }
  return { orientation: 1, hadGps: false };
}

function parseTiff(bytes: Uint8Array, start: number, end: number): ExtractedJpegMetadata {
  const little = text.decode(bytes.subarray(start, start + 2)) === 'II';
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u16 = (at: number) => view.getUint16(at, little);
  const u32 = (at: number) => view.getUint32(at, little);
  const firstIfd = start + u32(start + 4);
  if (firstIfd < start || firstIfd + 2 > end) return { orientation: 1, hadGps: false };
  let orientation: ExtractedJpegMetadata['orientation'] = 1;
  let hadGps = false;
  let capturedAt: string | undefined;
  const entries = Math.min(u16(firstIfd), 512);
  for (let index = 0; index < entries; index += 1) {
    const entry = firstIfd + 2 + index * 12;
    if (entry + 12 > end) break;
    const tag = u16(entry);
    if (tag === 0x0112) {
      const value = u16(entry + 8);
      if (value >= 1 && value <= 8) orientation = value as typeof orientation;
    }
    if (tag === 0x8825) hadGps = u32(entry + 8) !== 0;
    if (tag === 0x9003 || tag === 0x0132) {
      const count = u32(entry + 4);
      const valueOffset = count <= 4 ? entry + 8 : start + u32(entry + 8);
      if (valueOffset >= start && valueOffset + count <= end) {
        const raw = text
          .decode(bytes.subarray(valueOffset, valueOffset + count))
          .replace(/\0/g, '');
        const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(raw);
        if (match)
          capturedAt = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}`;
      }
    }
  }
  return { orientation, hadGps, ...(capturedAt ? { capturedAt } : {}) };
}

export function canonicalizeCaptureMetadata(input: {
  extracted: ExtractedJpegMetadata;
  sourceMimeType: string;
  sourceBytes: number;
  width?: number;
  height?: number;
}): CanonicalCaptureMetadata {
  return {
    orientation: input.extracted.orientation,
    hadGps: input.extracted.hadGps,
    sourceMimeType: input.sourceMimeType,
    sourceBytes: input.sourceBytes,
    ...(input.extracted.capturedAt ? { capturedAt: input.extracted.capturedAt } : {}),
    ...(input.width === undefined ? {} : { sourceWidth: input.width }),
    ...(input.height === undefined ? {} : { sourceHeight: input.height }),
  };
}
